import defer from 'lodash/defer';
import isEmpty from 'lodash/isEmpty';
import {formatUrl} from 'url-lib';
import {setWindowLocation} from 'js-utils/http';
import {keysCamelToSnake} from 'js-utils/transformation';
import queryString from 'querystring';
import {gettext} from '../../translations';
import {HAS_WINDOW} from 'eventbrite_design_system/utils/dom';

import {
    setLocationData,
    getBrowserLocation,
    getBrowserLocationPermission,
    getCanonicalSearchUrl,
    transformGeoPlaceObject,
    parseLocationResponse,
    storeQuery,
    constants as destinationConstants,
    location as locationDuck,
} from '@eb/destination-utils';

import * as analytics from '@eb/site-analytics';
import {
    getPathWithSanitizedQueryParams,
    constructObjectForUrl,
    constructPaginatedUrl,
} from '../utils/url';
import {trackPageView as trackPageViewAction} from './analytics';
import {
    GA_LOCATION_CHANGE_ACTION,
    GA_SEARCH_CATEGORY,
    GA_CITY_BROWSE_CATEGORY,
    LOCATION_STORAGE_KEY,
} from '../constants';
import {setIsLoading} from './page';
// eslint-disable-next-line import/no-cycle
import {searchInformationForPlace, searchByBBox} from './search';

const {
    actionTypes: {
        LOCATION_DENIED_CODE,
        UPDATE_LOCATION_INFORMATION,
        UPDATE_LOCATION_SUGGESTIONS,
    },
    actions: {
        setWaitingForLocation,
        showLocationDeniedNotification,
    },
    api: {
        getPlaceFromCoordinatesBase: getPlaceFromCoordinatesApi,
        searchLocationSuggestions: searchLocationSuggestionsApi,
    },
} = locationDuck;

const {
    GA_USER_CURRENT_LOCATION_ACTION,
    GA_USER_CURRENT_LOCATION_DENIED_LABEL,
    GA_USER_CURRENT_LOCATION_ERROR_LABEL,
} = destinationConstants;

const {
    trackEventFromStateOnPageUnload,
    trackEventFromState,
} = analytics;

export const updateLocationInformation = (path, search, location) => ({
    type: UPDATE_LOCATION_INFORMATION,
    payload: {
        path,
        search,
        location,
    },
});

export const updateWindowLocation = (path) => (
    (dispatch) => {
        dispatch(setIsLoading(true));

        /* This is necessary because safari will halt all JS execution on page redirect. In
        order to ensure that the loading animation is displayed we need to force the redirect
        to the bottom of the render loop. */
        defer(() => (setWindowLocation(path)));
    }
);

const updateLocationSuggestions = (suggestions) => ({
    type: UPDATE_LOCATION_SUGGESTIONS,
    payload: suggestions,
});

export const searchLocationSuggestions = (query) => (
    (dispatch, getState) => {
        if (!query) {
            return dispatch(updateLocationSuggestions([]));
        }

        const {app: {featureFlags}} = getState();

        return searchLocationSuggestionsApi(query)
            .then((response) => dispatch(updateLocationSuggestions(parseLocationResponse(response, featureFlags))));
    }
);

export const trackLocationChange = ({newLocation, action}) => (
    (dispatch, getState) => {
        const state = getState();
        const {location: {currentPlace}} = state;
        const label = `${currentPlace}-${newLocation}`;

        trackEventFromStateOnPageUnload(state, {
            category: GA_SEARCH_CATEGORY,
            action,
            label,
        });
    }
);

export const trackUserCurrentLocation = ({label, shouldRedirect}) => (
    (dispatch, getState) => {
        const state = getState();
        const gaData = {
            label,
            category: shouldRedirect ? GA_CITY_BROWSE_CATEGORY : GA_SEARCH_CATEGORY,
            action: GA_USER_CURRENT_LOCATION_ACTION,
        };

        if (shouldRedirect) {
            trackEventFromStateOnPageUnload(state, gaData);
        } else {
            trackEventFromState(state, gaData);
        }
    }
);

export const locationChange = ({
    slug,
    placeId,
    latitude,
    longitude,
    placeType,
    currentPlace,
    currentPlaceParent,
    isOnline = false,
    history,
}) => (
    (dispatch) => {
        let location = currentPlace;

        if (slug) {
            location = slug;
        } else if (currentPlaceParent) {
            location = `${currentPlace}, ${currentPlaceParent}`;
        }

        dispatch(trackLocationChange({newLocation: currentPlace, action: GA_LOCATION_CHANGE_ACTION}));

        const locationData = {
            slug,
            placeId,
            latitude,
            longitude,
            placeType,
            currentPlace,
            currentPlaceParent,
            isOnline,
        };

        /* When the user selects "online" from the Recent suggestions the placeId is null and the slug is online,
        but the isOnline is false. Because of that we should change it to reflect the correct filter */
        if (!placeId && slug === 'online') {
            locationData.isOnline = true;
        }

        if (HAS_WINDOW && !locationData.isOnline) {
            /*
            * Stores the current place data in the localStorage.
            * To store an object we have to replace the `,` with `;`
            * because we later reconstruct the data separating different
            * locations by `,` and the object itself should not be separated
            * until parsed as a JSON object.
            */
            storeQuery(
                JSON.stringify({
                    currentPlace,
                    currentPlaceParent,
                    content: currentPlace,
                    secondaryContent: currentPlaceParent,
                    value: `recent-${placeId}`,
                    latitude,
                    longitude,
                    placeType,
                    placeId,
                    slug,
                    isOnline,
                    isHistorySuggestion: true,
                }).replace(/,/g, ';'), LOCATION_STORAGE_KEY
            );
        }

        // See https://docs.google.com/document/d/1Vg-kyxM34gw71vw_5dLcfzFRhzM3scQ3FKtYUlWXLSA/edit?usp=sharing
        setLocationData(keysCamelToSnake(locationData));

        // dispatch(updateWindowLocation(formatUrl(`/d/${location}/events/`)));
        history.push(formatUrl(`/d/${location}/events/`));
    }
);

export const setUserCurrentLocationAction = (history, shouldRedirect, place) => (
    (dispatch) => {
        const action = shouldRedirect ? locationChange : searchInformationForPlace;

        const {
            currentPlace,
            ...placeAttrs
        } = place;

        dispatch(trackUserCurrentLocation({
            shouldRedirect,
            label: currentPlace,
        }));

        if ('bbox' in place) {
            dispatch(searchByBBox(history, place.bbox));
        } else {
            dispatch(action({
                currentPlace,
                ...placeAttrs,
                isUsingCurrentLocation: true,
                isOnline: false,
                history,
            }));
        }
    }
);

/*
* Action which enacts the browser get current location api and once that promise is resolved,
* gets a place object from the browser, then runs a search against that data.
*/
export const getUserCurrentLocation = (history, shouldRedirect = false) => (
    async(dispatch) => {
        const locationPermission = await getBrowserLocationPermission()
            .catch(() => 'permissionsNotSupported');

        if (locationPermission === 'denied') {
            dispatch(trackUserCurrentLocation({
                shouldRedirect,
                label: GA_USER_CURRENT_LOCATION_DENIED_LABEL,
            }));

            return dispatch(showLocationDeniedNotification());
        }

        dispatch(setWaitingForLocation(true));

        const browserLocation = await getBrowserLocation()
            .catch(({code}) => {
                if (code === LOCATION_DENIED_CODE && locationPermission === 'permissionsNotSupported') {
                    dispatch(showLocationDeniedNotification());
                }

                /* prompt is status returned from browser location permissions object
                when no permission is set for accessing user location
                https://developer.mozilla.org/en-US/docs/Web/API/Navigator/permissions */
                if (code === LOCATION_DENIED_CODE && locationPermission === 'prompt') {
                    dispatch(setWaitingForLocation(false));
                }

                /* tracking the type of error that occurred for GA */
                let label = GA_USER_CURRENT_LOCATION_ERROR_LABEL;

                if (code === LOCATION_DENIED_CODE) {
                    label = GA_USER_CURRENT_LOCATION_DENIED_LABEL;
                }

                dispatch(trackUserCurrentLocation({
                    shouldRedirect,
                    label,
                }));
            });

        if (!browserLocation) {
            return undefined;
        }

        const {latitude, longitude} = browserLocation.coords;

        let {place} = await getPlaceFromCoordinatesApi({latitude, longitude});

        if (!isEmpty(place)) {
            place = transformGeoPlaceObject(place);
        } else {
            // if we can't map the user to a city, we just resort to a rough
            // bounding box around the current location.
            place = {
                currentPlace: gettext('Your Location'),
                currentPlaceParent: '',
                latitude,
                longitude,
                isOnline: false,
                bbox: {
                    nw: {lng: longitude - 0.4, lat: latitude + 0.3},
                    sw: {lng: longitude - 0.4, lat: latitude - 0.3},
                    ne: {lng: longitude + 0.4, lat: latitude + 0.3},
                    se: {lng: longitude + 0.4, lat: latitude - 0.3},
                },
            };
        }

        return dispatch(setUserCurrentLocationAction(history, shouldRedirect, place));
    }
);

export const setSearchInformationFromUrl = (path, query) => (
    (dispatch, getState) => {
        const {
            search,
            location: locationFromState,
        } = getState();

        /* Create a path that contains url route and a whitelisted subset of query params which
        actually impact the search response value */
        const constructedPath = getPathWithSanitizedQueryParams(path, query);

        // get search data from state and save it into the state along with the associated path
        // state will look like
        /**
        location: {
            locationInformation: {
                '/d/ca--san-francisco/free--music--events--this-month/': {
                search: {
                        eventSearch: {
                            places: ['85922583'],
                            dates: 'this_month',
                            price: 'free',
                            tags: ['EventbriteCategory/103'],
                        },
                    },
                    location: {
                        slug: 'ca--san-francisco',
                    },
                },
                location: {
                    city: 'San Francisco',
                    region: 'CA',
                    slug: 'ca--san-francisco',
                    placeId: '85922583',
                },
            },
        }
        */

        dispatch(updateLocationInformation(
            constructedPath,
            search,
            locationFromState,
        ));
    }
);

export const updateSearchLocation = (history, {queryParamsToOmit = []} = {}) => (
    (dispatch, getState) => {
        const {
            search: {
                eventSearch,
            },
            location: {
                slug,
            },
            pagination: {
                currentPage,
            },
        } = getState();

        const path = getCanonicalSearchUrl({
            ...eventSearch,
            slug,
        });

        // translate the react-router v5 prop to this older one
        const locationBeforeTransitions = {
            query: queryString.parse(history.location.search.replace('?', '')),
            ...history.location,
        };

        const constructedPathAndQuery = constructObjectForUrl(
            path,
            eventSearch,
            locationBeforeTransitions,
            currentPage,
            queryParamsToOmit
        );

        const nextUrl = constructPaginatedUrl(currentPage, eventSearch, slug, history.location);

        dispatch(setSearchInformationFromUrl(path, constructedPathAndQuery.query));
        dispatch(trackPageViewAction());
        history.push(nextUrl);
    }
);
